export type Model = 'auto' | 'gpt-3.5-turbo' | 'gpt-4o' | 'gpt-4' | 'o1-preview' | 'llama-3.1-sonar-large-128k-online' | 'llama-3.1-sonar-huge-128k-online' | 'sonar' | 'sonar-pro' | 'sonar-reasoning' | 'sonar-reasoning-pro' | 'sonar-deep-research' | 'r1-1776' | 'power-ai-chosen-model' | 'gpt-image-1';

export type Theme = 'light' | 'dark';

export interface ImageGenerationResponse {
  success: boolean;
  model: string;
  image_data: string;
  format: string;
  saved_to_file: string;
  reference_images?: string[];
}

export interface Conversation {
  id: number;
  uuid: string;
  title: string | null;
  created_at?: string;
  updated_at?: string;
  context_type?: string | null;  // 'compare', 'pdf', etc - drives sidebar badges
  active_run_status?: 'running' | 'just_done' | null;  // server run state: running = live run, just_done = finished <90s ago
  // Teams UX iteration: GET /conversations carries these so the sidebar can
  // give every conversation exactly ONE home. `project_id` moves the row out
  // of the date groups and under its project (whatever its visibility, which
  // is why the shared list alone could never do this job). Optional: a backend
  // without the fields simply keeps every row in the date groups, i.e. today's
  // behaviour.
  project_id?: number | null;
  visibility?: 'private' | 'team';
}

// GET /conversations/uuid/{uuid}. Everything past the base row is Phase 2 of
// the Teams feature and therefore optional: against a backend that has not
// shipped it yet the viewer is simply treated as the owner (is_owner absent =>
// not read-only), which is exactly the pre-Teams behaviour.
export interface ConversationDetail extends Conversation {
  visibility?: 'private' | 'team';
  is_owner?: boolean;
  owner_email?: string;
  shared_team_id?: number | null;
  // Teams phase 3: the project this conversation belongs to (null/absent =
  // none). Drives the top-bar project pill.
  project_id?: number | null;
  playbook_run?: {
    id: number;
    playbook_uuid: string | null;
    playbook_name: string | null;
    version_number: number | null;
    status: string;
  } | null;
  // Present when this chat was forked from another. The crumb links back;
  // absent/null if this is not a branch or the parent was deleted.
  branched_from?: { uuid: string; title: string | null } | null;
}

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  model: Model;
  fileUrl?: string;
}

export interface MessageWithCitations extends Message {
  id: number;
  citations?: Set<string>;
  isComplete?: boolean;
  sources?: string[];
  created_at?: string;
  fileUrl?: string;
  fileUrls?: string[];
  error?: boolean;
  suggestions?: string[];  // Follow-up question chips (client-only; from the SSE "suggestions" event)
  creditsCharged?: number;  // Per-answer credits from the SSE "usage" event (client-only)
  modelsUsed?: { model: string; in_tokens: number; out_tokens: number; calls: number; cost_usd: number }[];  // Per-model token/cost breakdown from the SSE "usage" event (client-only)
  tps?: number;
  traceDurationMs?: number;
  fromServer?: boolean;  // True only for server-fetched messages (real DB id)
}

// Which wallet pays for this user's turns, straight from /auth/credit-info.
// TWO of them are team wallets, and pricing v3 spends them in this order:
//   'team_allowance' - the monthly Team plan jatah (resets every cycle)
//   'team_pool'      - the prepaid top-up pool (carries over)
// Any chrome that used to test === 'team_pool' must test isTeamBilled() instead,
// otherwise a team on a fresh plan reads as a personal/none user.
export type BillingSource = 'personal' | 'team_pool' | 'team_allowance' | 'none';

// The team block on /auth/credit-info (null/absent when the user has no team).
// Defined once here because the userInfo shape is duplicated across nine
// components; they all reference this type so the shapes cannot drift.
export interface UserTeamSummary {
  name: string;
  role: 'owner' | 'member';
  pool_balance: number;
  // Pricing v3 additions. Optional: a backend that has not shipped the monthly
  // plan yet simply omits them, and every reader defaults them to 0/false.
  allowance_remaining?: number; // credits left in the current plan cycle
  plan_active?: boolean;        // is that cycle still running
  member_monthly_used: number;
  member_monthly_limit: number | null;
}

// Is the signed-in user covered by a team wallet at all (either one)?
export const isTeamBilled = (source?: BillingSource | null): boolean =>
  source === 'team_pool' || source === 'team_allowance';

// ONE honest usable-credits number for the chrome: what the team can still
// spend today, whichever wallet it comes out of. The split (jatah bulanan vs
// top up) belongs in a tooltip and on the team panel, not in a 12px line.
export const teamUsableCredits = (team?: UserTeamSummary | null): number =>
  Math.round((team?.allowance_remaining ?? 0) + (team?.pool_balance ?? 0));

// Tooltip copy for the number above: "3.000 jatah bulanan + 5.226 top up".
export const teamCreditsBreakdown = (team?: UserTeamSummary | null): string => {
  const allowance = Math.round(team?.allowance_remaining ?? 0);
  const pool = Math.round(team?.pool_balance ?? 0);
  return (
    `${allowance.toLocaleString('id-ID')} jatah bulanan + ` +
    `${pool.toLocaleString('id-ID')} top up`
  );
};

export interface User {
  name: string;
  email: string;
  status: string;          // EFFECTIVE status (pool-covered members read as active)
  credits_used: number;
  subscription_status?: 'free' | 'active' | 'pro' | 'cancelled';
  // Team-aware credit fields. All optional: the frontend must keep working
  // against a backend that has not shipped them yet.
  credits_total?: number | null;   // personal limit; null when unlimited
  billing_source?: BillingSource;
  team?: UserTeamSummary | null;
  raw_status?: string;             // stored status before the effective-status rewrite
  has_team?: boolean;
}